// code-guardian-ignore-duplicates: parallel tab layout: the StoreMasterPage tab strip drawn inline on purpose so every tabbed page matches
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Target, ShoppingCart, SlidersHorizontal, LifeBuoy, Users, ListChecks, Settings2, Coins,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { salesTargetsAPI } from '../services/api';
import NoProjectSelectedState from '../components/NoProjectSelectedState';
import { BlockLoader } from '../components/loading';
import PeriodMonthPicker from '../components/PeriodMonthPicker';
import { PageErrorBanner } from '../components/PageErrorBanner';
import SalesTargetSettingsModal from '../components/SalesTargetSettingsModal';
import GridTab from './salesTargets/GridTab';
import MultipliersTab from './salesTargets/MultipliersTab';
import DefaultsTab from './salesTargets/DefaultsTab';
import ResultsTab from './salesTargets/ResultsTab';
import CoverageTab from './salesTargets/CoverageTab';
import IncentiveTab from './salesTargets/IncentiveTab';
import { currentMonth, type SalesTargetConfig } from './salesTargets/types';

/**
 * Master Data → Sales & Targets.
 *
 * ── WHAT THIS PAGE IS ────────────────────────────────────────────────────────
 * Promoters work inside someone else's shop, so a target can sensibly belong to
 * the SHOP or to the PERSON. The admin owns one side and the system derives the
 * other. Nobody types a derived number, and there is no "recalculate" button:
 * every figure on the Results tab is computed on read, so a redeployment changes
 * the answer the next time anyone looks.
 *
 * ── WHY ONE PAGE AND NOT SIX ─────────────────────────────────────────────────
 * The tabs are steps in one job, not six features. You upload targets, see what
 * the multipliers did to them, fix the ones the calculation refused, and check
 * what is still unset. Splitting those across nav entries would mean leaving the
 * page to cross-check — the lesson migration 076 records about Store Categories,
 * and the reason the Product Master is one entry with tabs.
 *
 * ── NOTHING HERE IS PROJECT-SPECIFIC ─────────────────────────────────────────
 * The products come from this project's Product Master, the fields a multiplier
 * may read come from this project's own store and employee custom fields, and
 * which side owns the numbers is this project's setting. A project that never
 * opens this page is unaffected by it existing.
 */

type TabKey = 'targets' | 'sales' | 'multipliers' | 'defaults' | 'results' | 'coverage' | 'incentive';

const TABS: { key: TabKey; label: string; icon: typeof Target; blurb: string }[] = [
  {
    key: 'targets',
    label: 'Targets',
    icon: Target,
    blurb: 'What has to be sold this month, per product. Upload a sheet or set a figure by hand; '
      + 'the other side of the book is worked out from it.',
  },
  {
    key: 'sales',
    label: 'Sales',
    icon: ShoppingCart,
    blurb: 'What was actually sold. Record it at month end, or let the system read it from the '
      + 'stock tasks your promoters already fill in.',
  },
  {
    key: 'multipliers',
    label: 'Multipliers',
    icon: SlidersHorizontal,
    blurb: 'How a store target becomes a promoter target. A store with two promoters cannot give '
      + 'each of them the whole store figure — the multiplier decides what each one carries.',
  },
  {
    key: 'defaults',
    label: 'Defaults',
    icon: LifeBuoy,
    blurb: 'What a store falls back to when its target was never set. A safety net, so a store '
      + 'missed in the upload still has a number to work to.',
  },
  {
    key: 'results',
    label: 'Results',
    icon: Users,
    blurb: 'What each promoter is actually held to — worked out day by day, so a mid-month move '
      + 'between stores splits correctly. With what they sold, once sales are in.',
  },
  {
    key: 'coverage',
    label: 'Coverage',
    icon: ListChecks,
    blurb: 'What has no target yet. The list to work through before the month starts.',
  },
  {
    key: 'incentive',
    label: 'Incentive',
    icon: Coins,
    blurb: 'What each promoter is paid for what they sold — by a grid of amounts per product and % '
      + 'of target, or by a rate for every unit sold.',
  },
];

export default function SalesTargetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);

  const fromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<TabKey>(
    TABS.some((t) => t.key === fromUrl) ? (fromUrl as TabKey) : 'targets',
  );
  // The period lives in the URL alongside the tab, so a link can point at one
  // month's problem and the back button steps between months rather than
  // leaving the page.
  const [month, setMonth] = useState<string>(searchParams.get('month') || currentMonth());

  const [config, setConfig] = useState<SalesTargetConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoadingConfig(true);
    setError(null);
    try {
      const res = await salesTargetsAPI.getConfig(selectedProjectId);
      setConfig(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load the sales target settings.');
    } finally {
      setLoadingConfig(false);
    }
  }, [selectedProjectId]);

  useEffect(() => { void loadConfig(); }, [loadConfig]);

  const updateUrl = useCallback((next: { tab?: TabKey; month?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.tab) params.set('tab', next.tab);
    if (next.month) params.set('month', next.month);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectTab = (tab: TabKey) => { setActiveTab(tab); updateUrl({ tab }); };
  const selectMonth = (value: string) => { setMonth(value); updateUrl({ month: value }); };

  const current = useMemo(() => TABS.find((t) => t.key === activeTab) ?? TABS[0], [activeTab]);

  if (!selectedProjectId) {
    return (
      <NoProjectSelectedState message="Select a project from the dropdown in the menu bar to set its targets." />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales &amp; Targets</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">{current.blurb}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* The period is page-level, not per tab: every tab answers a question
              about one month, and making each carry its own picker would let two
              tabs disagree about which month you are looking at. */}
          <PeriodMonthPicker
            value={month}
            onChange={(e) => selectMonth(e.target.value)}
            label="Month"
            htmlFor="sales-target-month"
          />
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Settings2 className="h-4 w-4" />
            Settings
          </button>
        </div>
      </div>

      {error && <PageErrorBanner message={error} />}

      <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === activeTab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => selectTab(t.key)}
              className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {loadingConfig || !config ? (
        <BlockLoader className="rounded-xl border border-gray-200 bg-white py-20" />
      ) : (
        <>
          {activeTab === 'targets' && (
            <GridTab
              mode="target"
              projectId={selectedProjectId}
              month={month}
              config={config}
              onConfigChange={setConfig}
              onOpenResults={() => selectTab('results')}
            />
          )}
          {activeTab === 'sales' && (
            <GridTab
              mode="sales"
              projectId={selectedProjectId}
              month={month}
              config={config}
              onConfigChange={setConfig}
              onOpenResults={() => selectTab('results')}
            />
          )}
          {activeTab === 'multipliers' && (
            <MultipliersTab projectId={selectedProjectId} config={config} />
          )}
          {activeTab === 'defaults' && (
            <DefaultsTab projectId={selectedProjectId} config={config} />
          )}
          {activeTab === 'results' && (
            <ResultsTab projectId={selectedProjectId} month={month} config={config} />
          )}
          {activeTab === 'coverage' && (
            <CoverageTab projectId={selectedProjectId} month={month} config={config} />
          )}
          {activeTab === 'incentive' && (
            <IncentiveTab projectId={selectedProjectId} month={month} config={config} />
          )}
        </>
      )}

      {/* Rendered only once the config is loaded: the modal seeds its controls
          from it, and mounting with nulls would show a form of empty radios. */}
      {config && (
        <SalesTargetSettingsModal
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onSaved={(next) => { setConfig(next); setSettingsOpen(false); }}
          projectId={selectedProjectId}
          config={config}
        />
      )}
    </div>
  );
}
